import { Flex } from "@similarweb/ui-components/foundations";

import { getEducationData, useCurrentPageState } from "app/components/educationbar/education-utils";
import { translate } from "app/filters/i18nFilter";
import { AI_AGENT_MEETING, COMPANY_ANALYSIS_SIGNALS } from "app/pages/sales-intelligence/constants/routes";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import companyOverviewStorage from "app/pages/sales-intelligence/modules/company-analysis/services/storage/storage";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import HelpButton from "app/pages/sales-intelligence/pages/company-analysis/components/shared/HelpButton/HelpButton";
import HelpLink from "app/pages/sales-intelligence/pages/company-analysis/components/shared/HelpLink/HelpLink";
import PageHeaderBreadcrumbs from "app/pages/sales-intelligence/pages/company-analysis/containers/header/PageHeaderBreadcrumbs/PageHeaderBreadcrumbs";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { useEducationBarState } from "app/pages/sales-intelligence/sub-modules/education-bar";
import EducationBarContainer from "app/pages/sales-intelligence/sub-modules/education-bar/containers/EducationBarContainer";
import type { ISubNavProps } from "app/components/sub-navigation-bar/SubNav";
import { SubNav } from "app/components/sub-navigation-bar/SubNav";
import GradientButton from "app/pages/sales-intelligence/modules/ai-agent/components/GradientButton/GradientButton";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { SIGNALS_HELP_ARTICLE_LINK } from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type PageHeaderProps = Omit<ISubNavProps, "topLeftComponent, topRightComponent">;

const CompanyPageHeader = (props: PageHeaderProps) => {
    const { state, params } = useCurrentStateAndParamsWithFixedDomain();

    const educationId = useCurrentPageState();
    const hasEducationData = getEducationData(educationId);
    const isAiMeetingAgentEnabled = salesSettingsHelper.isAiMeetingAgentEnabled;

    const { isBarVisible, onClose, onOpen } = useEducationBarState(companyOverviewStorage);

    const renderEducationBar = () => {
        if (!isBarVisible || !hasEducationData) {
            return null;
        }

        return (
            <EducationBarContainer
                onClose={onClose}
                stateId={educationId || ""}
            />
        );
    };

    return (
        <SubNav
            {...props}
            topLeftComponent={<PageHeaderBreadcrumbs />}
            topRightComponent={
                <Flex
                    alignItems="center"
                    gap="8px"
                    marginX="16px"
                >
                    <DataCreditsIndicatorContainer />
                    {isAiMeetingAgentEnabled ? (
                        <GradientButton
                            variant="ghost"
                            onClick={() => {
                                void proNavigator.go(AI_AGENT_MEETING, { domain: params.key });
                                companyAnalysisTrackingService.trackPrepareForMeetingButtonClick(params.key);
                            }}
                            dataAutomation="company-page-header-meeting-button"
                        >
                            {translate("si.company-analysis.company.ai-agent.meeting.button")}
                        </GradientButton>
                    ) : null}
                    {isBarVisible || !hasEducationData || state.stateId === COMPANY_ANALYSIS_SIGNALS ? null : (
                        <HelpButton onClick={onOpen} />
                    )}
                    {state.stateId === COMPANY_ANALYSIS_SIGNALS ? (
                        <HelpLink
                            dataAutomation="signals-help-icon"
                            href={SIGNALS_HELP_ARTICLE_LINK}
                            tooltipContent={translate("si.signals.help.tooltip.text")}
                        />
                    ) : null}
                </Flex>
            }
            education={renderEducationBar()}
        />
    );
};

export default CompanyPageHeader;
